import {
    Component,
    Input,
    Output,
    OnInit,
    EventEmitter,
    HostListener,
    ViewChild,
    ElementRef,
    AfterViewInit,
    ViewContainerRef,
    OnDestroy
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { switchMap, takeUntil } from 'rxjs/operators';
import { from, Subject } from 'rxjs';
import get from 'lodash-es/get';

import { ConfigService, } from '@finder/core/config.service';
import {
    MapData,
    MapDataLocationMarker,
    MapAnimationEvent,
    MarkerOptions,
    MapInterface
} from '@finder/shared/components/map/map/map.interface';
import { ViewportDetection } from '@finder/shared/services/viewport-detection/viewport-detection.service';
import { MAP_DEFAULT_ID } from '@finder/shared/components/map/defaults/map.defaults.constants';
import { MAP_ANIMATIONS } from './maps.animations';
import { KEYBOARD } from '@finder/shared/constants/user-input.constants';
import { LazyLoaderService } from '@finder/shared/services/lazy-loader/lazy-loader-service';

@Component({
    selector: 'finder-detail-map',
    templateUrl: './maps.component.html',
    styleUrls: ['./maps.component.scss'],
    animations: [...MAP_ANIMATIONS]
})
export class MapsComponent implements OnInit, AfterViewInit, OnDestroy {

    @Input() public mapData: MapData;
    @Input() public expanded = false;
    @Input() public hideCloseButton = false;
    @Output() public mapExpanded = new EventEmitter<MapAnimationEvent>();
    @Output() public mapIsReady: EventEmitter<MapInterface> = new EventEmitter();

    @ViewChild('mapWrapper', { read: ElementRef }) mapWrapper: ElementRef;
    @ViewChild('finderMap', { read: ViewContainerRef })
    private finderMap: ViewContainerRef;

    defaults;
    markers: Array<MapDataLocationMarker>;
    center: MapDataLocationMarker;
    maxBounds;
    paradeRoute: Array<MapDataLocationMarker>;
    link: string;
    linkText: string;
    closeButtonText: string;
    title: string;
    showPopup = false;
    markerOptions: MarkerOptions = {};

    private destroy$ = new Subject<void>();

    constructor(
        private configService: ConfigService,
        private viewport: ViewportDetection,
        private sanitizer: DomSanitizer,
        private lazyLoader: LazyLoaderService
    ) {

    }

    /**
     * Retracts map and notifies to subscribers
     */
    @HostListener('keydown.escape', ['$event'])
    retractMap($event) {
        this.sendEvent(false);

        if ($event.type === KEYBOARD.KEY_DOWN) {
            // Focus on the map element
            this.mapWrapper.nativeElement.focus();
        }
    }

    // Ignore the if just for coverage, there's no need to test unsubscribe
    /* istanbul ignore next */
    ngOnDestroy() {
        /**
         * Don't try and clean up the map here. [finder-map] will clean itself up
         */
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngAfterViewInit() {
        from(this.lazyLoader.lazyLoadModule('finder-map', this.finderMap, {
            defaults: this.defaults,
            markers: this.markers,
            center: this.center,
            paradeRoute: this.paradeRoute,
            maxBounds: this.maxBounds,
            markerOptions: this.markerOptions
        }))
        .pipe(
            switchMap((data: any) => data.instance.mapIsReady),
            takeUntil(this.destroy$)
        )
        .subscribe((mapObj: MapInterface) => this.mapIsReady.emit(mapObj));
    }

    ngOnInit(): void {
        // Set the defaults
        this.defaults = this.mapData.location.defaults;

        // Create the marker
        this.markers = this.mapData.location.markers;

        // Look for route information and add it to the map
        this.paradeRoute = get(this.mapData, 'location.paradeRoute');

        // Setup the marker options
        this.markerOptions = {
            showPopup: Array.isArray(this.paradeRoute),
            keyboard: !!this.paradeRoute
        };

        // Center the map on the marker
        this.center = get(this.mapData, 'location.markers[0]');

        if (this.center) {
            // Avoiding undefined errors
            this.center.zoom = this.defaults.zoom;
        }

        // Remove the zoom controls and panning
        this.defaults.addZoomControl = false;
        this.defaults.dragging = false;

        // Set the link information
        this.linkText = this.hideCloseButton ? '' : this.mapData.linkText;
        this.link = this.getMapLink();

        // Set the close button text
        this.closeButtonText = this.mapData.closeButtonText;

        // Set the title
        this.title = this.mapData.title;

        // Expand the map if it's being expanded from the outside
        if (this.expanded) {
            this.expandMap();
        }
    }

    handleClick(event) {
        // If we have a parade route expand the map
        if (this.paradeRoute) {
            event.preventDefault();
            this.expandMap();
        }
    }

    /**
     * Expands map and notifies subscribers
     */
    expandMap() {
        // Only on desktop the map should expand
        if (this.viewport.isDesktop()) {
            this.sendEvent(true);
        }
    }

    private getMapLink(): string {
        let link = `${this.configService.getValue('syndicatedLayout.baseUrl')}/${this.mapData.link}`;
        if (this.configService.isDevelopment()) {
            link = `/${this.mapData.link}`;
        }

        return link;
    }

    private sendEvent(state) {
        this.expanded = state;

        // Emit event so any host can react to it
        this.mapExpanded.emit({
            state: this.expanded,
            mapId: MAP_DEFAULT_ID
        });
    }
}
